import { AddressInp } from './Common';

export interface Vehicle {
  /** @format int32 */
  id?: number;

  /** user-defined unique identifier of a vehicle */
  custom_id?: string;

  /** name of vehicle */
  name?: string;

  /** addional identifier (e.g. car license plate) */
  plate?: string;

  /** defines speeds and restrictions on routes */
  vehicle_class?: 'none' | 'car' | 'truck' | 'van';

  /** The Vehicletype can be referenced from a vehicle. Every empty field of the vehicle then uses the corresponding type of the vehicletype. */
  vehicletype?: Vehicletype;
  depot_location?: AddressInp;

  /**
   * reference to Depot address entity. Either this or use the full depot address above. Only one can be used
   * @format int32
   */
  depot_location_id?: number;

  /**
   * capacity corresponding to load type 1
   * @format int32
   */
  capacity_1?: number;

  /**
   * capacity corresponding to load type 2
   * @format int32
   */
  capacity_2?: number;

  /**
   * capacity corresponding to load type 3
   * @format int32
   */
  capacity_3?: number;

  /**
   * fixed cost of a vehicle which is used in a tour
   * @format float
   */
  daily_rate?: number;

  /**
   * variable cost of vehicle per driven kilometer
   * @format float
   */
  km_rate?: number;

  /**
   * variable cost of idle time
   * @format float
   */
  waiting_hour_rate?: number;

  /**
   * factor for vehicle speed (speed as predicted by HERE road and traffic conditions and vehicle_class)
   * @format float
   */
  traveltime_factor?: number;

  /**
   * earliest start time of tour (departure of driver), ISO 8601 time (not date-time format)
   * @format date-time
   */
  departure_time_from?: string;

  /**
   * latest start time of tour (departure of driver), ISO 8601 time (not date-time format)
   * @format date-time
   */
  departure_time_to?: string;

  /**
   * latest end time of tour (return of driver, or last delivery), ISO 8601 time (not date-time format)
   * @format date-time
   */
  operation_end?: string;

  /** Should the vehicle return to the depot? */
  roundtrip?: boolean;

  /**
   * maximum tour duration in minutes (lower priority than operation_end)
   * @format int32
   */
  max_tour_duration?: number;

  /** defines whether the vehicle is used, effective at start of route calculaton */
  active?: boolean;

  /** name of the assigned LoadClass */
  load_class?: string;

  /** defines if the vehicle only drives within its territories (true) or if it can go to any area = zbV vehicle (false) */
  obeys_territory_mapping?: boolean;

  /** List of territory custom ids the vehicle should be assigned to. Only works when obeys_territory_mapping = true */
  territory_custom_ids?: string[];

  /**
   * maximum load height of the vehicle
   * @format float
   */
  max_load_height?: number;

  /** Defines if the vehicle has a liftgate */
  liftgate?: boolean;

  /** Defines if the vehicle has a trailer */
  trailer?: boolean;
  variable_els_seconds_per_load?: number[];

  /** els values for each load dimension used for loading and unloading at the depot */
  depot_els_config?: {
    loading_els_config?: { fixed_els_seconds?: number; variable_els_seconds_per_load?: number[] };
    unloading_els_config?: { fixed_els_seconds?: number; variable_els_seconds_per_load?: number[] };
  };
}

/**
 * The Vehicletype can be referenced from a vehicle. Every empty field of the vehicle then uses the corresponding type of the vehicletype.
 */
export interface Vehicletype {
  /** @format int32 */
  id?: number;

  /** name of the vehicle type */
  vehicletypename?: string;

  /** informal description */
  description?: string;

  /** defines speeds and restrictions on routes */
  default_vehicle_class?: 'none' | 'car' | 'truck' | 'van';

  /**
   * capacity corresponding to load type 1
   * @format int32
   */
  default_capacity_1?: number;

  /**
   * capacity corresponding to load type 2
   * @format int32
   */
  default_capacity_2?: number;

  /**
   * capacity corresponding to load type 3
   * @format int32
   */
  default_capacity_3?: number;

  /**
   * fixed cost of a vehicle which is used in a tour
   * @format float
   */
  default_daily_rate?: number;

  /**
   * variable cost of vehicle per driven kilometer
   * @format float
   */
  default_km_rate?: number;

  /**
   * variable cost of idle time
   * @format float
   */
  default_waiting_hour_rate?: number;

  /**
   * factor for vehicle speed (speed as predicted by HERE road and traffic conditions and vehicle_class)
   * @format float
   */
  default_tt_factor?: number;

  /**
   * earliest start time of tour (departure of driver), ISO 8601 time (not date-time format)
   * @format date-time
   */
  default_departure_time_from?: string;

  /**
   * latest start time of tour (departure of driver), ISO 8601 time (not date-time format)
   * @format date-time
   */
  default_departure_time_to?: string;

  /**
   * latest end time of tour (return of driver, or last delivery), ISO 8601 time (not date-time format)
   * @format date-time
   */
  default_return_time_to?: string;

  /**
   * maximum tour duration in minutes (lower priority than operation_end)
   * @format int32
   */
  default_max_tour_duration?: number;

  /** should the vehicle return to the depot */
  default_roundtrip?: boolean;

  /** does vehicletype have a liftgate? */
  default_liftgate?: boolean;

  /** does vehicletype have a trailer? */
  default_trailer?: boolean;

  /** is the vehicletype allowed to carry ADR Points ("Gefahrgut")? */
  default_adr_allowed?: boolean;

  /**
   * maximum load height of the vehicletype
   * @format float
   */
  default_max_load_height?: number;

  /** The load class of the vehicletype, see LoadClass for more information */
  load_class?: string;

  /**
   * fixed length of stay at a stop for that certain vehicletype
   * @format int32
   */
  default_fixed_els_seconds?: number;

  /** a list of variable els per load item for all three load dimensions */
  default_variable_els_seconds_per_load?: number[];

  /** els values for each load dimension used for loading and unloading at the depot */
  default_depot_els_config?: {
    loading_els_config?: { fixed_els_seconds?: number; variable_els_seconds_per_load?: number[] };
    unloading_els_config?: { fixed_els_seconds?: number; variable_els_seconds_per_load?: number[] };
  };
}

export interface VehicleFilter {
  num_results: number,
  page: number,
  show: boolean,
  hasMore: boolean,
  reset: boolean,
  q: string,
  options: any
}

export interface VehicleSettings {
  defaultSpeedLimit: number
  maintenanceInterval: number
}

export interface Vehicles {
  fixed_service_time_s?: number | null;
  variable_service_time_s_per_load?: number[] | null;
  name: string;
  vehicle_routing_type: string;
  fixed_cost_eur: number;
  variable_cost_eur_per_km: number;
  capacities: number[];
  travel_time_factor: number | null;
  operation_start_window: Record<string, any> | null;
  max_working_time_min: number | null;
  max_allowed_distance_km: number | null;
  operation_end: string | null;
  round_trip: boolean | null;
  has_liftgate: boolean;
  has_trailer: boolean;
  max_adr_points: number;
  max_load_height_cm: number | null;
  max_load_length_cm: number | null;
  max_load_width_cm: number | null;
  depot_service_time_config: Record<string, any>;
  start_location: Record<string, any> | null;
  end_location: Record<string, any> | null;
  road_weight_limit_kg: number | null;
  width_cm: number | null;
  height_cm: number | null;
  length_cm: number | null;
  load_class_name: string | null;
  vehicle_type: string | null;
  primary_territory_names: string[] | null;
  secondary_territory_names: string[] | null;
  is_active: boolean;
  id: string;
  notes: string | null;
}


export interface TimeWindow {
  start: string | null
  end: string | null
}

export type VehicleRoutingType = 'car' | 'truck' | 'van'

export const VEHICLE_ROUTING_TYPES: VehicleRoutingType[] = [
  'car',
  'truck',
  'van',
];

export interface VehicleSettingsResponse {
  operation_start_window: TimeWindow | null
  fixed_cost_eur: number | null
  variable_cost_eur_per_km: number | null
  max_working_time_min: number | null
  operation_end: string | null
  round_trip: boolean | null
  vehicle_routing_type: VehicleRoutingType
  depot_service_time_config: {
    loading_service_time_config: {
      fixed_service_time_s: number
      variable_service_time_s_per_load: number[]
    }
    unloading_service_time_config: {
      fixed_service_time_s: number
      variable_service_time_s_per_load: number[]
    }
  }
  variable_service_time_s_per_load: number[]
  fixed_service_time_s: number
  load_class_name: string | null
}
